"use client";
import CookieConsent from "@/components/common/cookie-consent";
import { useIsomorphicLayoutEffect } from "@/hooks/use-isomorphic-layout-effect";
import { cookieName, Lang } from "@/i18n/settings";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";

export type LocaleCtxProps = {
  lang: Lang;
  inactiveLang?: string | null;
  onLangChange: () => void;
  isChangingLang: boolean;
  setHide: (value: boolean) => void;
  hide: boolean;
};

export const LocaleContext = createContext<LocaleCtxProps>({} as LocaleCtxProps);

export const useLocaleCtx = () => useContext(LocaleContext);

export function LocaleProvider({ children, locale }: { children: React.ReactNode; locale: Lang }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [hide, setHide] = useState(false);

  const [cookies, setCookie] = useCookies([cookieName]);
  const [inactiveLang, setInactiveLang] = useState<string | null>();
  const [isChangingLang, setIsChangingLang] = useState(false);

  function onLangChange() {
    setIsChangingLang(true);

    const sParams = searchParams.toString();
    const newLang = locale === "ar" ? "en" : "ar";
    setCookie(cookieName, newLang, { path: "/" });

    let newUrl = pathname.replace(`/${locale}`, `/${newLang}`);

    if (sParams) {
      newUrl = newUrl + "?" + sParams;
    }

    router.replace(newUrl, {
      scroll: true,
    });
  }

  useIsomorphicLayoutEffect(() => {
    if (!cookies.i18next || cookies.i18next !== locale) {
      setCookie(cookieName, locale, { path: "/" });
    }

    if (locale === "ar") {
      setInactiveLang("EN");
    } else {
      setInactiveLang("ع");
    }
    setIsChangingLang(false);
  }, [locale]);

  useEffect(() => {
    // Hide the switch lang button in news page
    if (pathname.match(/\/news\/[a-zA-Z0-9]+/)) {
      setHide(true);
    } else {
      setHide(false);
    }
  }, [pathname]);

  return (
    <LocaleContext.Provider
      value={{
        lang: locale,
        inactiveLang,
        onLangChange,
        isChangingLang,
        setHide,
        hide,
      }}
    >
      {children}

      <CookieConsent lang={locale} />
    </LocaleContext.Provider>
  );
}
